/**
 * Hijri (Qamari) calendar.
 *
 * Primary: the Umm al-Qura calendar, the civil Hijri calendar of Saudi Arabia
 * and the closest widely available approximation of observed lunar
 * visibility. Month lengths are irregular (29 or 30 days in a non-repeating
 * pattern), so - exactly like the Solar Hijri year - it is read from ICU
 * rather than from a hard-coded table.
 *
 * Fallback: the tabular (arithmetic, Kuwaiti) calendar, used only on runtimes
 * without full ICU. It is exact for most dates but can differ by a day near
 * month boundaries, so {@link HIJRI_CALENDAR} reports which one is active.
 */

import { icuToJdn, icuJdnTo, supportsCalendar } from "./icu.js";

export type HijriCalendar = "umalqura" | "tabular";

export type HijriDate = { hy: number; hm: number; hd: number };

/** Umm al-Qura tables are published for 1300-1600 AH. */
export const HIJRI_MIN_YEAR = 1300;
export const HIJRI_MAX_YEAR = 1600;

const UMALQURA_AVAILABLE = supportsCalendar("islamic-umalqura");

/** Which Hijri calendar this runtime actually resolves dates with. */
export const HIJRI_CALENDAR: HijriCalendar = UMALQURA_AVAILABLE
  ? "umalqura"
  : "tabular";

export function detectHijriCalendar(): HijriCalendar {
  return HIJRI_CALENDAR;
}

// Kuwaiti tabular arithmetic calendar.
const HIJRI_EPOCH_JDN = 1948440; // 1 Muharram 1 AH
const mod = (a: number, b: number): number => a - Math.floor(a / b) * b;

function tabularToJdn(hy: number, hm: number, hd: number): number {
  return (
    Math.floor((11 * hy + 3) / 30) +
    354 * hy +
    30 * hm -
    Math.floor((hm - 1) / 2) +
    hd +
    HIJRI_EPOCH_JDN
  );
}

function tabularFromJdn(jdn: number): HijriDate {
  // Estimate the year, then let the two correction loops snap to the year
  // that actually contains `jdn`. At most a couple of iterations each.
  let hy = Math.floor((30 * (jdn - HIJRI_EPOCH_JDN) + 10646) / 10631);
  while (hy > 1 && tabularToJdn(hy, 12, 1) > jdn) hy -= 1;
  while (tabularToJdn(hy + 1, 1, 1) <= jdn) hy += 1;

  let hm = 1;
  while (hm < 12 && tabularToJdn(hy, hm + 1, 1) <= jdn) hm += 1;
  return { hy, hm, hd: jdn - tabularToJdn(hy, hm, 1) + 1 };
}

export function hijriToJdn(
  hy: number,
  hm: number,
  hd: number,
  calendar: HijriCalendar = HIJRI_CALENDAR,
): number {
  assertHijriYear(hy, calendar);
  if (calendar === "tabular") return tabularToJdn(hy, hm, hd);
  return icuToJdn({ year: hy, month: hm, day: hd }, "islamic-umalqura");
}

export function jdnToHijri(
  jdn: number,
  calendar: HijriCalendar = HIJRI_CALENDAR,
): HijriDate {
  if (calendar === "tabular") return tabularFromJdn(jdn);
  const d = icuJdnTo(jdn, "islamic-umalqura");
  return { hy: d.year, hm: d.month, hd: d.day };
}

function assertHijriYear(hy: number, calendar: HijriCalendar): void {
  const lo = calendar === "umalqura" ? HIJRI_MIN_YEAR : 1;
  const hi = calendar === "umalqura" ? HIJRI_MAX_YEAR : 1600;
  if (hy < lo || hy > hi) {
    throw new RangeError(
      `Hijri year ${hy} is outside the supported range (${lo}-${hi} AH)`,
    );
  }
}

export function isHijriLeapYear(
  hy: number,
  calendar: HijriCalendar = HIJRI_CALENDAR,
): boolean {
  if (calendar === "tabular") {
    return mod(mod(11 * hy + 14, 30) + 1, 30) < 11;
  }
  return (
    hijriToJdn(hy + 1, 1, 1, calendar) - hijriToJdn(hy, 1, 1, calendar) === 355
  );
}

/** Days in a Hijri month - always 29 or 30. */
export function hijriMonthLength(
  hy: number,
  hm: number,
  calendar: HijriCalendar = HIJRI_CALENDAR,
): number {
  if (hm < 1 || hm > 12) throw new RangeError("Invalid Hijri month: " + hm);
  const next = hm === 12 ? { y: hy + 1, m: 1 } : { y: hy, m: hm + 1 };
  return (
    hijriToJdn(next.y, next.m, 1, calendar) -
    hijriToJdn(hy, hm, 1, calendar)
  );
}

export function isValidHijri(
  hy: number,
  hm: number,
  hd: number,
  calendar: HijriCalendar = HIJRI_CALENDAR,
): boolean {
  if (!Number.isInteger(hy) || !Number.isInteger(hm) || !Number.isInteger(hd)) {
    return false;
  }
  if (hm < 1 || hm > 12 || hd < 1) return false;
  if (calendar === "umalqura" && (hy < HIJRI_MIN_YEAR || hy > HIJRI_MAX_YEAR)) {
    return false;
  }
  try {
    return hd <= hijriMonthLength(hy, hm, calendar);
  } catch {
    return false;
  }
}
